import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { Contribution } from '../hooks/useContributions';
import Select from '../components/ui/Select.tsx';
import AsyncMemberSelect from '../components/ui/AsyncMemberSelect.tsx';

interface Member {
  id: number;
  name: string;
  phone_number: string;
}

interface MemberOption {
  value: number;
  label: string;
}

interface DriveOption {
  value: number;
  label: string;
}

interface ContributionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    member_id: number;
    amount: number;
    contribution_date: string;
    notes: string;
    is_merry_go_round:boolean;
    welfare_plan_id?: number;
    contribute_to_multiple_drives?:boolean;
    distribute_equally?:boolean;
    drive_id?: number;
    drives?:Array<number> | Array<object>;
  }) => void;
  contribution?: Contribution;
  isEditing?: boolean;
  isLoading?: boolean;
}

const ContributionModal = ({
  isOpen,
  onClose,
  onSubmit,
  contribution,
  isEditing = false,
  isLoading = false,
}: ContributionModalProps) => {
  const [formData, setFormData] = useState({
    member_id: '',
    amount: '',
    contribution_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [isMerryGoRound, setIsMerryGoRound] = useState(false); 
  const [welfarePlans, setWelfarePlans] = useState([]);
  const [selectedWelfarePlan, setSelectedWelfarePlan] = useState<any>(null);
  const [selectedDrive, setSelectedDrive] = useState<DriveOption | null>(null);
  const [contributeToMultipleDrives, setContributeToMultipleDrives] = useState(false);
  const [distributeEqually, setDistributeEqually] = useState(false);
  const [selectedDrives, setSelectedDrives] = useState<DriveOption[]>([]);
  const [driveAmounts, setDriveAmounts] = useState<{ [key: number]: number }>({});
  const [selectedMember, setSelectedMember] = useState<MemberOption | null>(null);
  const [driveSelectionError, setDriveSelectionError] = useState('');

  useEffect(() => {
    if (contribution) {
      setFormData({
        member_id: contribution.member.id.toString(),
        amount: contribution.amount.toString(),
        contribution_date: contribution.contribution_date,
        notes: contribution.reference || '',
      });
      // Set drive if contribution has one
      if (contribution.drive) {
        setSelectedDrive({ value: 0, label: contribution.drive }); // Using drive string for now
      }
      setSelectedMember({
        value: contribution.member.id,
        label: `${contribution.member.name} - ${contribution.member.phone_number}`,
      });
    } else {
      setFormData({
        member_id: '',
        amount: '',
        contribution_date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      setSelectedDrive(null);
      setSelectedMember(null);
    }
  }, [contribution]);

  useEffect(() => {
    if (formData.member_id) {
      api.get(`/members/${formData.member_id}/welfare-plans`).then(res => {
        if (res.data.success) {
          // Filter for accepted member status AND open welfare plan status
          const activeAcceptedPlans = res.data.data.filter((wp: any) => 
            wp.status === 'accepted' && wp.welfare_plan_status === 'open'
          );
          setWelfarePlans(activeAcceptedPlans);
          setSelectedWelfarePlan(null); // reset on member change
        } else {
          setWelfarePlans([]);
          setSelectedWelfarePlan(null);
        }
      }).catch(() => {
        setWelfarePlans([]);
        setSelectedWelfarePlan(null);
      });
    } else {
      setWelfarePlans([]);
      setSelectedWelfarePlan(null);
    }
  }, [formData.member_id]);

  const { data: drivesData } = useQuery({
    queryKey: ['contribution-drives'],
    queryFn: async () => {
      const { data } = await api.get('/contribution-drives');
      return data.data;
    },
  });

  const driveOptions: DriveOption[] = Array.isArray(drivesData) ? drivesData.map((drive: any) => ({
    value: drive.id,
    label: drive.title,
  })) : [];

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setDriveSelectionError('');

    if (contributeToMultipleDrives) {
      if (selectedDrives.length < 2) {
        setDriveSelectionError('Select at least 2 drives, or uncheck "Contribute to Multiple Drives" to pick just one.');
        return;
      }
      if (!distributeEqually) {
        const sum = selectedDrives.reduce((total, drive) => total + (driveAmounts[drive.value] || 0), 0);
        const total = Number(formData.amount) || 0;
        if (Math.round(sum * 100) !== Math.round(total * 100)) {
          setDriveSelectionError(`The per-drive amounts add up to Ksh ${sum.toFixed(2)}, which must match the total contribution of Ksh ${total.toFixed(2)}.`);
          return;
        }
      }
    }

    const payload: any = {
      member_id: Number(formData.member_id),
      amount: Number(formData.amount),
      contribution_date: formData.contribution_date,
      notes: formData.notes,
      is_merry_go_round: isMerryGoRound,
      contribute_to_multiple_drives: contributeToMultipleDrives,
      distribute_equally: distributeEqually,
  };
    if (selectedWelfarePlan) {
      payload.welfare_plan_id = selectedWelfarePlan.value;
    }
    if (contributeToMultipleDrives) {
      payload.drives = distributeEqually
        ? selectedDrives.map((drive) => ({ drive_id: drive.value }))
        : selectedDrives.map((drive) => ({
            drive_id: drive.value,
            amount: driveAmounts[drive.value] || 0,
          }));
    } else if (selectedDrive) {
      payload.drive_id = selectedDrive.value;
    }
    onSubmit(payload);
  };

  if (!isOpen) return null;

return (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
      <h2 className="text-xl font-bold mb-4">
        {isEditing ? 'Edit Contribution' : 'Add New Contribution'}
      </h2>
      <form onSubmit={handleSubmit}>
        {/* Member */}
        <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="member">
              Member
            </label>
            <AsyncMemberSelect
              id="member"
              value={selectedMember}
              onChange={(selectedOption: any) => {
                setSelectedMember(selectedOption);
                setFormData({ ...formData, member_id: selectedOption?.value.toString() || '' });
              }}
              className="text-base"
              classNamePrefix="select"
              placeholder="Select a member..."
              required
            />
          </div>

        {/* Welfare Plan */}
          {welfarePlans.length > 0 && (
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="welfarePlan">
                Welfare Plan (Open & Accepted)
              </label>
              <Select
                id="welfarePlan"
                value={selectedWelfarePlan}
                onChange={(option: any) => setSelectedWelfarePlan(option)}
                options={welfarePlans.map((wp: any) => ({ value: wp.welfare_plan_id, label: wp.welfare_plan_name }))}
                className="text-base"
                classNamePrefix="select"
                isSearchable={true}
                placeholder="Select a welfare plan..."
              />
            </div>
          )}

        {/* New Checkbox for Merry-go-round */}
          <div className="mb-6 flex items-center">
            <input
              type="checkbox"
              id="merryGoRound"
              checked={isMerryGoRound}
              onChange={(e) => setIsMerryGoRound(e.target.checked)}
              className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="text-gray-700 text-sm font-bold" htmlFor="merryGoRound">
              Merry-go-round Contribution
            </label>
          </div>
          {/* End New Checkbox */}

        {/* Checkbox: Multiple Drives */}
        <div className="mb-4 flex items-center">
          <input
            type="checkbox"
            id="multipleDrives"
            checked={contributeToMultipleDrives}
            onChange={(e) => {
              setContributeToMultipleDrives(e.target.checked);
              setDriveSelectionError('');
              if (!e.target.checked) {
                setSelectedDrives([]);
                setDriveAmounts({});
                setDistributeEqually(false);
              }
            }}
            className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
          <label className="text-gray-700 text-sm font-bold" htmlFor="multipleDrives">
            Contribute to Multiple Drives?
          </label>
        </div>

        {/* Multiple Drives */}
        {contributeToMultipleDrives ? (
          <>
            {/* Distribute Equally */}
            <div className="mb-4 flex items-center">
              <input
                type="checkbox"
                id="distributeEqually"
                checked={distributeEqually}
                onChange={(e) => {
                  setDistributeEqually(e.target.checked);
                  setDriveSelectionError('');
                }}
                className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label className="text-gray-700 text-sm font-bold" htmlFor="distributeEqually">
                Distribute Equally Across Selected Drives
              </label>
            </div>

            {/* Select Drives */}
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">Select Drives</label>
              <Select
                isMulti
                options={driveOptions}
                value={selectedDrives}
                onChange={(selected: any) => {
                  setSelectedDrives(selected || []);
                  setDriveSelectionError('');
                }}
                classNamePrefix="select"
                placeholder="Select drives..."
              />
              {driveSelectionError && (
                <p className="mt-1 text-sm text-red-600">{driveSelectionError}</p>
              )}
            </div>

            {/* Drive Amounts */}
            {!distributeEqually && selectedDrives.length > 0 && (
              <div className="mb-4 space-y-2">
                {selectedDrives.map((drive) => (
                  <div key={drive.value} className="flex justify-between items-center">
                    <span className="text-gray-700 text-sm">{drive.label}</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Amount"
                      value={driveAmounts[drive.value] || ''}
                      onChange={(e) => {
                        setDriveAmounts({
                          ...driveAmounts,
                          [drive.value]: parseFloat(e.target.value) || 0,
                        });
                        setDriveSelectionError('');
                      }}
                      className="w-32 border rounded px-2 py-1 text-right"
                      required
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          // Single Drive Selection
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">Contribution Category/Drive</label>
            <Select
              value={selectedDrive}
              onChange={(option: DriveOption | null) => setSelectedDrive(option)}
              options={driveOptions}
              classNamePrefix="select"
              placeholder="Select a contribution category..."
            />
          </div>
        )}

        {/* Amount */}
        <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="amount">
              Amount (Ksh)
            </label>
            <input
              type="number"
              id="amount"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
              min="0"
              step="0.01"
            />
          </div>

        {/* Date */}
        <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="date">
              Contribution Date
            </label>
            <input
              type="date"
              id="date"
              value={formData.contribution_date || new Date().toISOString().split('T')[0]}
              onChange={(e) => setFormData({ ...formData, contribution_date: e.target.value })}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="notes">
              Notes
            </label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
              rows={3}
            />
          </div>

        {/* Buttons */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            {isEditing ? 'Update' : 'Add'} Contribution
          </button>
        </div>
      </form>
    </div>
  </div>
);
};

export default ContributionModal; 